import React, { useState } from 'react';
import { Users, Calendar, MessageCircle } from 'react-feather';
import {
  InlineShell,
  InlineEyebrow,
  CardButton,
  CardErrorNotice,
  hashHue,
  type F8CardData,
  type OnAction,
} from './cardHelpers';
// PPL-508 — the circles gate: one field, one way out.
import {
  firstNameGateMessage,
  FIRST_NAME_ACTION_LABEL,
  PROFILE_EDIT_PATH,
} from '../../../utils/circleGate';

export default function CircleDetailCard({
  card,
  onAction,
}: {
  card: F8CardData;
  onAction?: OnAction;
}) {
  const data = card?.data || {};
  const circleId: string = data.circleId;
  const name: string = data.name || 'Circle';
  const description: string = data.description || '';
  const rules: string = data.rules || '';
  const memberCount: number = data.memberCount || 0;
  const max: number = data.max || 50;
  const tags: string[] = Array.isArray(data.tags) ? data.tags : [];
  const isMember: boolean = !!data.isMember;
  const hue = hashHue(name);
  const initial = (name[0] || '?').toUpperCase();
  // PPL-508 — the join was fire-and-forget: `handleCardAction` re-throws and
  // nothing caught it, so a refusal was invisible. It now lands under the row.
  const [joinError, setJoinError] = useState<{ text: string; gate: boolean } | null>(null);

  const handleJoin = async () => {
    setJoinError(null);
    try {
      await onAction?.({
        action: 'cloud_function',
        params: {
          functionName: 'joinCircleRequestEurW',
          args: { circleId },
        },
      });
    } catch (err: any) {
      setJoinError({
        text: firstNameGateMessage(err) || err?.message || 'could not join this circle',
        gate: firstNameGateMessage(err) !== null,
      });
    }
  };

  const heroStyle: React.CSSProperties = {
    background: `linear-gradient(135deg,
      oklch(0.50 0.22 ${hue}) 0%,
      oklch(0.55 0.24 ${(hue + 30) % 360}) 50%,
      oklch(0.65 0.26 ${(hue + 60) % 360}) 100%)`,
  };

  return (
    <InlineShell>
      <InlineEyebrow label="circle" trailing={isMember ? 'joined' : undefined} />

      <div className="f8c-circle-detail-head">
        <div className="f8c-circle-detail-hero" style={heroStyle}>
          <span className="f8c-circle-detail-initial">{initial}</span>
        </div>
        <div className="f8c-circle-detail-identity">
          <div className="f8c-circle-detail-name">{name}</div>
          <div className="f8c-circle-detail-meta">
            <Users size={12} />
            <span>{memberCount}/{max} members</span>
          </div>
        </div>
      </div>

      {description && <p className="f8c-circle-detail-desc">{description}</p>}

      {tags.length > 0 && (
        <div className="f8c-circle-detail-tags">
          {tags.map((tag) => (
            <span key={tag} className="f8c-profile-interest">
              {tag}
            </span>
          ))}
        </div>
      )}

      {rules && (
        <div className="f8c-circle-detail-rules">
          <div className="f8c-circle-detail-rules-label">Rules</div>
          <p className="f8c-circle-detail-rules-text">{rules}</p>
        </div>
      )}

      <div className="f8c-circle-detail-actions">
        {!isMember && (
          <CardButton primary onClick={handleJoin}>
            Join circle
          </CardButton>
        )}
        {isMember && (
          <CardButton
            primary
            onClick={() =>
              onAction?.({
                action: 'navigate',
                params: { screen: 'CircleChat', circleId },
              })
            }
          >
            <MessageCircle size={13} />
            Chat
          </CardButton>
        )}
        <CardButton
          onClick={() =>
            onAction?.({
              action: 'skill_invoke',
              params: { message: `show me events in ${name}` },
            })
          }
        >
          <Calendar size={13} />
          Events
        </CardButton>
        <CardButton
          small
          onClick={() =>
            onAction?.({
              action: 'navigate',
              params: { screen: 'CircleDetail', circleId },
            })
          }
        >
          View
        </CardButton>
      </div>

      <CardErrorNotice
        text={joinError?.text}
        actionLabel={joinError?.gate ? FIRST_NAME_ACTION_LABEL : null}
        actionTo={joinError?.gate ? PROFILE_EDIT_PATH : null}
      />
    </InlineShell>
  );
}
